/**
 * Canvas 2D map engine (OpenLayers) for GPU-less VMs / Mesa llvmpipe hosts
 * where MapLibre WebGL fails with BindToCurrentSequence.
 *
 * Streaming model (same idea as QGIS):
 *   - Never download the whole COG — only XYZ tiles for the current viewport.
 *   - Cap concurrent tile fetches so the browser stays interactive.
 *   - Do not reload tiles while the user is panning/zooming.
 *   - DEM colour stretch is provisional first, then refined in the background.
 */
import OlMap from 'ol/Map.js';
import View from 'ol/View.js';
import TileLayer from 'ol/layer/Tile.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorTileLayer from 'ol/layer/VectorTile.js';
import VectorSource from 'ol/source/Vector.js';
import XYZ from 'ol/source/XYZ.js';
import { defaults as defaultControls } from 'ol/control/defaults.js';
import { defaults as defaultInteractions } from 'ol/interaction/defaults.js';
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom.js';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom.js';
import Draw from 'ol/interaction/Draw.js';
import Modify from 'ol/interaction/Modify.js';
import Translate from 'ol/interaction/Translate.js';
import Snap from 'ol/interaction/Snap.js';
import Select from 'ol/interaction/Select.js';
import type Interaction from 'ol/interaction/Interaction.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj.js';
import type { Extent } from 'ol/extent.js';
import type BaseLayer from 'ol/layer/Base.js';

import type {
  EngineAdapter,
  Layer,
  LayerKind,
  VectorExportFeature,
  ViewerCamera,
  ViewerHostContext,
} from '@geosoft/viewer-sdk';
import type { PolygonSymbolState } from '@/components/vector/polygonSymbolRegistry';
import type { UniqueValueSymbology } from '@/components/vector/vectorStyleModel';
import type { LineSymbolState } from '@/components/vector/lineSymbolRegistry';
import {
  loadPreferredBasemapId,
  basemapTileUrls,
  type BasemapId,
} from '@/engines/maplibre/basemaps';
import {
  ensureTitilerPngTileTemplate,
  normalizeTitilerSourceHost,
  toBrowserTitilerUrl,
  withTitilerTileJsonPngFormat,
} from '@/utils/titilerSourceHost';
import {
  RASTER_TILE_GRID_SIZE,
  requestTileSize,
  tilePixelRatioFor,
  withDisplayTileSize,
} from '@/utils/rasterTileResolution';
import { attachHillshadeTileResolution } from '@/utils/hillshade';
import {
  colorizeDemTileTemplate,
  isDemLikeLayer,
} from '@/utils/titilerDemColorize';
import {
  clearCanvasMapHostHandle,
  setCanvasMapHostHandle,
} from '@/engines/openlayers/hostHandle';
import {
  applyTitilerRasterStyleToTemplate,
  openLayersCanvasFilterFromState,
  openLayersCompositeFromBlend,
} from '@/components/raster/applyRasterToolCard';
import { attachShadedReliefTileLoader } from '@/components/raster/shadedReliefBlend';
import { attachBackgroundMaskTileLoader } from '@/components/raster/backgroundMask';
import type { RasterBlendMode, RasterColorRamp } from '@/state/useViewerStore';
import { isSyntheticBasemapLayer } from '@/app/defaultBasemap';
import {
  createGeoJsonVectorLayer,
  createPmtilesVectorTileLayer,
  vectorStyleFunctionFromPaint,
} from '@/engines/openlayers/olVectorLayers';
import { isPickedVectorLayerId, vectorPickIdFrom } from '@/engines/openlayers/olVectorPick';
import {
  applyIdentifyCursor,
  isHighlightActive,
  type VectorHighlightTarget,
} from '@/components/vector/vectorIdentifyHighlight';
import {
  editCursorForTool,
  resolveVectorEditSession,
  type VectorEditTool,
} from '@/components/vector/vectorEditMode';
import type { IdentifyGeometryKind } from '@/components/vector/vectorIdentifyModel';
import {
  getVectorSessionFeaturesForLayer,
  type VectorEditResult,
} from '@/components/vector/vectorEditSession';
import { scaleFeaturesFrom, type ScaleSourceFeature } from '@/components/vector/scaleFeatureSource';
import {
  isPointIconId,
  loadPointIcons,
  peekPointIcon,
} from '@/components/vector/pointIconRegistry';
import {
  createPathProfileOverlayLayer,
  pathProfileOverlayFeatures,
  type PathProfileVertex,
} from '@/engines/openlayers/olPathProfileOverlay';

import 'ol/ol.css';

const SUPPORTED: LayerKind[] = ['raster', 'terrain', 'vector'];

/** Soft-VM defaults: keep the main thread free for interaction. */
const MAX_TILES_LOADING = 2;
const TILE_CACHE_SIZE = 32;
const TILE_SIZE = RASTER_TILE_GRID_SIZE;
/** Deepest tile zoom to request; OpenLayers overzooms beyond this. */
const MAX_RASTER_ZOOM = 22;
const CAMERA_EMIT_MS = 350;

function deriveTitilerTileJsonUrl(tilesUrl: string, preferTileSize?: number): string | null {
  const match = tilesUrl.match(
    /^(.*?)\/cog\/tiles\/([^/]+)\/\{z\}\/\{x\}\/\{y\}(?:\.[a-zA-Z]+)?(\?.*)?$/,
  );
  if (!match) return null;
  const [, base, tileMatrixSet, query] = match;
  let tilejson = withTitilerTileJsonPngFormat(
    `${base}/cog/${tileMatrixSet}/tilejson.json${query ?? ''}`,
  );
  if (preferTileSize) {
    const sep = tilejson.includes('?') ? '&' : '?';
    if (!tilejson.includes('tilesize=')) tilejson = `${tilejson}${sep}tilesize=${preferTileSize}`;
  }
  return tilejson;
}

/** OpenLayers XYZ wants `{x}{y}{z}` templates; MapLibre uses the same. */
function toOlTileUrl(template: string): string {
  return toBrowserTitilerUrl(normalizeTitilerSourceHost(template));
}

function createBasemapLayer(url: string, zIndex = 0): TileLayer {
  return new TileLayer({
    source: new XYZ({
      url,
      crossOrigin: 'anonymous',
      maxZoom: 16,
      transition: 0,
      interpolate: false,
    }),
    properties: { geosoftId: '__basemap__' },
    zIndex,
    preload: 0,
    cacheSize: TILE_CACHE_SIZE,
    useInterimTilesOnError: false,
  });
}

function createXyzSource(url: string, minZoom: number, maxZoom: number): XYZ {
  const source = new XYZ({
    url, 
    crossOrigin: 'anonymous',
    minZoom,
    maxZoom: Math.max(maxZoom, minZoom + 1),
    tileSize: TILE_SIZE,
    // HiDPI templates return 512px images for the 256px grid; without this OL
    // would treat each one as covering 4× the ground.
    tilePixelRatio: tilePixelRatioFor(url),
    transition: 0,
    interpolate: false,
  });
  // A hillshade template cannot state its own ground resolution; the tile
  // coordinate carries it (see utils/hillshade.ts).
  attachHillshadeTileResolution(source, url);
  attachShadedReliefTileLoader(source, url);
  // Takes over the load function when background keying is on; its fetch path
  // blends shaded relief internally so the two never clobber each other.
  attachBackgroundMaskTileLoader(source, url);
  return source;
}

type CanvasRenderListener = (event: { context?: CanvasRenderingContext2D }) => void;

/**
 * `prerender` / `postrender` are render-event names OL only declares on the
 * concrete layer classes, so a `BaseLayer`-typed handle cannot bind them.
 */
/**
 * Interpret the small MapLibre-style filter expressions the Vector
 * ToolCard emits (`==` / `in` / `any`+empty). Anything else is treated
 * as "show all" so a bad expression cannot hide the whole layer.
 */
function olFeatureToExport(feature: {
  getId?: () => unknown;
  getProperties?: () => Record<string, unknown>;
  getGeometry?: () => unknown;
}): {
  readonly id?: string | number;
  readonly properties: Readonly<Record<string, unknown>>;
  readonly geometry?: unknown;
} {
  const raw = (feature.getProperties?.() ?? {}) as Record<string, unknown>;
  const properties: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (key === 'geometry' || (value !== null && typeof value === 'object')) continue;
    properties[key] = value;
  }
  const id = feature.getId?.() ?? properties.OBJECTID_1 ?? properties.OBJECTID ?? properties.id;
  return {
    id: typeof id === 'string' || typeof id === 'number' ? id : undefined,
    properties,
    geometry: feature.getGeometry?.() ?? undefined,
  };
}

/**
 * Convert an OL feature's geometry into lon/lat GeoJSON, exactly what the
 * `vectorEditSession` store and the persistent session overlay both expect.
 * OpenLayers holds geometries in the map projection (EPSG:3857 here), so we
 * cannot ship the raw geometry into a store that assumes EPSG:4326.
 */
function olEditFeatureToGeoJsonLonLat(feature: {
  getId?: () => unknown;
  getProperties?: () => Record<string, unknown>;
  getGeometry?: () => unknown;
}): {
  readonly id?: string | number;
  readonly properties: Readonly<Record<string, unknown>>;
  readonly geometry?: unknown;
} {
  const base = olFeatureToExport(feature);
  const raw = feature.getGeometry?.();
  if (!raw) return base;
  try {
    const format = new GeoJSON();
    const written = format.writeGeometryObject(raw as Parameters<typeof format.writeGeometryObject>[0], {
      featureProjection: 'EPSG:3857',
      dataProjection: 'EPSG:4326',
    });
    return { ...base, geometry: written };
  } catch {
    return base;
  }
}

function featureMatchesSimpleFilter(
  properties: Readonly<Record<string, unknown>>,
  filter: unknown,
): boolean {
  if (!filter || !Array.isArray(filter) || filter.length === 0) return true;
  const op = filter[0];
  if (op === '==' && Array.isArray(filter[1]) && filter[1][0] === 'get') {
    const key = String(filter[1][1] ?? '');
    return String(properties[key] ?? '') === String(filter[2] ?? '');
  }
  if (op === 'in' && Array.isArray(filter[2]) && filter[2][0] === 'to-string') {
    const getExpr = filter[2][1];
    const key = Array.isArray(getExpr) && getExpr[0] === 'get' ? String(getExpr[1] ?? '') : '';
    return String(properties[key] ?? '').includes(String(filter[1] ?? ''));
  }
  if (op === 'any') {
    return filter.slice(1).some((clause) => featureMatchesSimpleFilter(properties, clause));
  }
  if (op === '!' && Array.isArray(filter[1]) && filter[1][0] === 'has') {
    const key = String(filter[1][1] ?? '');
    return properties[key] == null || String(properties[key]).trim() === '';
  }
  return true;
}

function asRenderEventTarget(layer: BaseLayer): {
  on(type: string, listener: CanvasRenderListener): void;
  un(type: string, listener: CanvasRenderListener): void;
} {
  return layer as unknown as {
    on(type: string, listener: CanvasRenderListener): void;
    un(type: string, listener: CanvasRenderListener): void;
  };
}

export class CanvasMapAdapter implements EngineAdapter {
  readonly id = 'openlayers' as const;

  private map: OlMap | null = null;
  private host: ViewerHostContext | null = null;
  /** Every mounted layer (raster tiles, vector tiles, GeoJSON) by scene id. */
  private layersById = new globalThis.Map<string, BaseLayer>();
  private editInteractions: Interaction[] = [];
  private editSelect: Select | null = null;
  private editWorkingLayer: VectorLayer | null = null;
  private editWorkingLayerId: string | null = null;
  private editOnEdit: ((result: VectorEditResult) => void) | null = null;
  /**
   * Permanent overlay that mirrors `vectorEditSession` for the current layer.
   * Kept separate from `editWorkingLayer` so that switching tools (which tears
   * down interactions) never wipes drawn polygons from view. See
   * `renderSessionOverlay` / `hydrateSessionOverlayFromStore`.
   */
  private sessionOverlayLayer: VectorLayer | null = null;
  private sessionOverlayLayerId: string | null = null;
  /** Coarse health per layer so the scene-tree dot is truthful on this engine. */
  private layerHealth = new globalThis.Map<
    string,
    'ready' | 'loading' | 'failed' | 'unsupported'
  >();
  /** Archive extents (WGS84) for mounted vector layers — used by zoom-to-layer. */
  private vectorBounds = new globalThis.Map<
    string,
    readonly [number, number, number, number]
  >();
  /** Last visibility requested per layer id, survives unmount/remount cycles. */
  private desiredVisibility = new globalThis.Map<string, boolean>();
  /** Live TiTiler template per layer — rewritten by the Raster ToolCard. */
  private rasterTileTemplates = new globalThis.Map<string, string>();
  /** Zoom range retained so a source swap keeps the same min/max. */
  private rasterZoom = new globalThis.Map<string, { min: number; max: number }>();
  /** Bound prerender listeners so we can replace them on re-apply. */
  private rasterPrerender = new globalThis.Map<
    string,
    { prerender: (event: { context?: CanvasRenderingContext2D }) => void; postrender: (event: { context?: CanvasRenderingContext2D }) => void }
  >();
  private basemapLayers: TileLayer[] = [];
  private fitted = false;
  private moveTimer: ReturnType<typeof setTimeout> | null = null;
  /** Live cursor lon/lat subscribers for Spectral hover sampling. */
  private pointerListeners = new Set<(ll: readonly [number, number] | null) => void>();
  private pointerMoveHandler: ((event: { coordinate?: number[]; dragging?: boolean }) => void) | null = null;
  private pointerLeaveHandler: (() => void) | null = null;
  /** Path Profile route overlay (vertices + line + graph cursor). */
  private pathProfileOverlay: VectorLayer | null = null;
  /** Last vector identify hit — attributes for View Info. */
  private lastVectorPickProperties: Readonly<Record<string, unknown>> | null = null;
  private vectorPaintById = new globalThis.Map<
    string,
    {
      readonly fillColor?: string;
      readonly fillOpacity?: number;
      readonly lineColor?: string;
      readonly lineWidth?: number;
      readonly lineSymbol?: LineSymbolState;
      readonly pointSize?: number;
      readonly pointSymbolId?: string;
      readonly pointRotation?: number;
      readonly polygonSymbol?: PolygonSymbolState;
      readonly uniqueValueSymbology?: UniqueValueSymbology | null;
    }
  >();
  private vectorHighlightById = new globalThis.Map<string, VectorHighlightTarget>();
  private identifyCursorArmed = false;

  supports(kind: LayerKind): boolean {
    return SUPPORTED.includes(kind);
  }

  async mount(host: ViewerHostContext): Promise<void> {
    this.host = host;
    host.container.replaceChildren();
    host.container.style.position = 'relative';
    host.container.style.width = '100%';
    host.container.style.height = '100%';
    host.container.style.background = '#D9E2EC';

    const basemapId = loadPreferredBasemapId();
    const basemapUrls = basemapTileUrls(basemapId);
    const layers: BaseLayer[] = [];
    this.basemapLayers = basemapUrls.map((url, index) =>
      createBasemapLayer(url, index),
    );
    layers.push(...this.basemapLayers);

    this.map = new OlMap({
      target: host.container,
      layers,
      pixelRatio: 1,
      maxTilesLoading: MAX_TILES_LOADING,
      // Instant wheel zoom — animated zoom redraws the whole canvas every frame
      // on CPU and is what feels like a multi-second UI freeze.
      interactions: defaultInteractions({
        mouseWheelZoom: false,
        doubleClickZoom: true,
        shiftDragZoom: false,
        pinchRotate: false,
      }).extend([
        new MouseWheelZoom({
          duration: 0,
          timeout: 30,
          constrainResolution: true,
        }),
      ]),
      view: new View({
        center: fromLonLat([55.27, 25.2]),
        zoom: 9,
        maxZoom: 16,
        constrainResolution: true,
        smoothResolutionConstraint: false,
      }),
      controls: defaultControls({ attribution: false, zoom: true, rotate: false }),
    });

    setCanvasMapHostHandle(host.container, this);

    // Raster ToolCard pixel inspector listens for `select` events with a
    // lon/lat point (same contract as MapLibre). Vector View Info uses the
    // same click: hit-test a district / point first, otherwise emit `__map__`.
    this.map.on('singleclick', (event) => {
      if (!this.host) return;
      const [lon, lat] = toLonLat(event.coordinate);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
      const hit = this.pickVectorFeatureAtPixel(event.pixel);
      if (hit) {
        this.lastVectorPickProperties = hit.properties;
        this.host.emit({
          type: 'select',
          selection: {
            layerId: hit.layerId,
            featureId: hit.featureId,
            point: [lon, lat],
          },
        });
        return;
      }
      this.lastVectorPickProperties = null;
      this.host.emit({
        type: 'select',
        selection: {
          layerId: '__map__',
          point: [lon, lat],
        },
      });
    });

    // Spectral / Location hover — local subscribers only (no React store storm).
    this.pointerMoveHandler = (event) => {
      if (this.identifyCursorArmed) {
        applyIdentifyCursor([this.map?.getViewport() ?? null, this.host?.container], true);
      }
      if (event.dragging || !event.coordinate) return;
      const [lon, lat] = toLonLat(event.coordinate);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
      for (const listener of this.pointerListeners) listener([lon, lat]);
    };
    this.pointerLeaveHandler = () => {
      for (const listener of this.pointerListeners) listener(null);
    };
    this.map.on('pointermove', this.pointerMoveHandler);
    this.map.getViewport().addEventListener('mouseleave', this.pointerLeaveHandler);

    // Only publish camera after the user stops moving — avoids React re-render storms.
    this.map.on('moveend', () => {
      if (!this.map || !this.host) return;
      if (this.moveTimer) clearTimeout(this.moveTimer);
      this.moveTimer = setTimeout(() => {
        if (!this.map || !this.host) return;
        const view = this.map.getView();
        const center = view.getCenter();
        if (!center) return;
        const [lon, lat] = toLonLat(center);
        this.host.emit({
          type: 'cameraChange',
          camera: { lonLat: [lon, lat], zoom: view.getZoom() ?? 9 },
        });
      }, CAMERA_EMIT_MS);
    });

    const view = this.map.getView();
    const center = view.getCenter();
    if (center) {
      const [lon, lat] = toLonLat(center);
      host.emit({
        type: 'cameraChange',
        camera: { lonLat: [lon, lat], zoom: view.getZoom() ?? 9 },
      });
    }

    console.info('[CanvasMapAdapter] mounted (Canvas 2D / streamed tiles)', {
      basemapId,
      maxTilesLoading: MAX_TILES_LOADING,
    });
  }

  async unmount(): Promise<void> {
    if (this.moveTimer) clearTimeout(this.moveTimer);
    this.moveTimer = null;
    if (this.map && this.pointerMoveHandler) {
      this.map.un('pointermove', this.pointerMoveHandler);
    }
    if (this.map && this.pointerLeaveHandler) {
      try {
        this.map.getViewport().removeEventListener('mouseleave', this.pointerLeaveHandler);
      } catch {
        /* viewport may already be gone */
      }
    }
    this.pointerMoveHandler = null;
    this.pointerLeaveHandler = null;
    this.pointerListeners.clear();
    this.clearPathProfileOverlay();
    if (this.sessionOverlayLayer && this.map) {
      try {
        this.map.removeLayer(this.sessionOverlayLayer);
      } catch {
        /* already removed */
      }
    }
    this.sessionOverlayLayer = null;
    this.sessionOverlayLayerId = null;
    if (this.host?.container) {
      clearCanvasMapHostHandle(this.host.container);
    }
    this.map?.setTarget(undefined);
    this.map = null;
    this.host = null;
    this.layersById.clear();
    this.desiredVisibility.clear();
    this.rasterTileTemplates.clear();
    this.rasterZoom.clear();
    this.rasterPrerender.clear();
    this.vectorBounds.clear();
    this.layerHealth.clear();
    this.basemapLayers = [];
    this.fitted = false;
    this.lastVectorPickProperties = null;
    this.vectorPaintById.clear();
    this.vectorHighlightById.clear();
    this.clearVectorEditInteractions();
    this.setIdentifyCursor(false);
  }

  /**
   * Subscribe to live map cursor lon/lat for Spectral hover sampling.
   * Returns an unsubscribe function.
   */
  subscribePointerLonLat(
    listener: (ll: readonly [number, number] | null) => void,
  ): () => void {
    this.pointerListeners.add(listener);
    return () => {
      this.pointerListeners.delete(listener);
    };
  }

  /**
   * Path picking for the Raster ToolCard's Path Profile: each click adds a
   * vertex, a double-click closes the route. OpenLayers suppresses
   * `singleclick` on a double-click, so the closing point is reported once.
   * Double-click zoom is parked for the duration so finishing the path does
   * not also zoom the map.
   */
  subscribePathProfilePicks(handlers: {
    readonly onPoint: (lonLat: readonly [number, number]) => void;
    readonly onFinish: (lonLat: readonly [number, number]) => void;
  }): (() => void) | null {
    const map = this.map;
    if (!map) return null;

    const toLonLatPoint = (
      coordinate: number[] | undefined,
    ): readonly [number, number] | null => {
      if (!coordinate) return null;
      const [lon, lat] = toLonLat(coordinate);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
      return [lon, lat];
    };

    const onSingleClick = (event: { coordinate?: number[] }) => {
      const point = toLonLatPoint(event.coordinate);
      if (point) handlers.onPoint(point);
    };
    const onDoubleClick = (event: { coordinate?: number[] }) => {
      const point = toLonLatPoint(event.coordinate);
      if (point) handlers.onFinish(point);
    };

    const doubleClickZoom = map
      .getInteractions()
      .getArray()
      .find((interaction): interaction is DoubleClickZoom => interaction instanceof DoubleClickZoom);
    const zoomWasActive = doubleClickZoom?.getActive() ?? false;
    doubleClickZoom?.setActive(false);

    const viewport = map.getViewport();
    const previousCursor = viewport.style.cursor;
    viewport.style.cursor = 'crosshair';

    map.on('singleclick', onSingleClick);
    map.on('dblclick', onDoubleClick);

    return () => {
      map.un('singleclick', onSingleClick);
      map.un('dblclick', onDoubleClick);
      if (zoomWasActive) doubleClickZoom?.setActive(true);
      viewport.style.cursor = previousCursor;
    };
  }

  renderPathProfileOverlay(
    vertices: readonly PathProfileVertex[],
    cursor: PathProfileVertex | null,
  ): void {
    if (!this.map) return;
    if (!this.pathProfileOverlay) {
      this.pathProfileOverlay = createPathProfileOverlayLayer();
      this.map.addLayer(this.pathProfileOverlay);
    }
    const source = this.pathProfileOverlay.getSource();
    source?.clear();
    source?.addFeatures(pathProfileOverlayFeatures(vertices, cursor));
  }

  clearPathProfileOverlay(): void {
    if (!this.pathProfileOverlay) return;
    this.pathProfileOverlay.getSource()?.clear();
    this.map?.removeLayer(this.pathProfileOverlay);
    this.pathProfileOverlay = null;
  }

  /** Swap the Canvas 2D basemap (used by BasemapSwitcher). */
  setBasemap(id: BasemapId): void {
    if (!this.map) return;
    for (const layer of this.basemapLayers) {
      this.map.removeLayer(layer);
    }
    this.basemapLayers = [];
    const urls = basemapTileUrls(id);
    for (let i = 0; i < urls.length; i += 1) {
      const layer = createBasemapLayer(urls[i], i);
      this.map.getLayers().insertAt(i, layer);
      this.basemapLayers.push(layer);
    }
  }

  /**
   * Mount a layer, then re-assert the last requested visibility.
   *
   * Raster mounts await TileJSON and TiTiler stats, so a hide can arrive while
   * the mount is still in flight; OpenLayers layers are created visible, which
   * used to leave the raster painted with the eye switched off.
   */
  async addLayer(layer: Layer): Promise<void> {
    try {
      await this.mountLayer(layer);
    } finally {
      if (this.desiredVisibility.get(layer.id) === false) {
        await this.setLayerVisible(layer.id, false);
      }
    }
  }

  private async mountLayer(layer: Layer): Promise<void> {
    if (!this.map) throw new Error('CanvasMapAdapter not mounted');
    if (!this.supports(layer.kind)) {
      this.layerHealth.set(layer.id, 'unsupported');
      return;
    }
    this.layerHealth.set(layer.id, 'loading');

    if (layer.kind === 'vector') {
      await this.mountVectorLayer(layer);
      return;
    }

    const tilesTemplate =
      (typeof layer.endpoints.tiles === 'string' && layer.endpoints.tiles)
      || (typeof layer.endpoints.terrain === 'string' && layer.endpoints.terrain)
      || null;
    if (!tilesTemplate) {
      console.warn('[CanvasMapAdapter] raster layer has no tiles endpoint', layer.id);
      this.layerHealth.set(layer.id, 'unsupported');
      return;
    }

    // Skip synthetic basemap catalog layers — we already paint one basemap.
    if (isSyntheticBasemapLayer(layer)) {
      this.layerHealth.set(layer.id, 'ready');
      return;
    }

    if (this.layersById.has(layer.id)) {
      // Re-adding an already mounted layer must not override a pending hide.
      if (this.desiredVisibility.get(layer.id) !== false) {
        await this.setLayerVisible(layer.id, true);
      }
      this.layerHealth.set(layer.id, 'ready');
      return;
    }

    let url = toOlTileUrl(tilesTemplate);
    let minZoom = layer.visibility?.minZoom ?? 0;
    let maxZoom = Math.min(MAX_RASTER_ZOOM, layer.visibility?.maxZoom ?? MAX_RASTER_ZOOM);
    let extent3857: Extent | undefined;
    let fitBbox = layer.bbox;

    const tilejsonUrl = deriveTitilerTileJsonUrl(url, requestTileSize());
    if (tilejsonUrl) {
      try {
        const res = await fetch(tilejsonUrl, { signal: AbortSignal.timeout(4000) });
        if (res.ok) {
          const tj = (await res.json()) as {
            tiles?: string[];
            bounds?: [number, number, number, number];
            minzoom?: number;
            maxzoom?: number;
          };
          if (tj.tiles?.[0]) {
            // Bounds/zoom from TileJSON; keep colorize params from the layer
            // template when present. Only adopt tj.tiles when we still lack a
            // TiTiler template (non-DEM XYZ).
            if (!url.includes('/cog/tiles/')) {
              url = ensureTitilerPngTileTemplate(toOlTileUrl(tj.tiles[0]));
            }
          }
          if (typeof tj.minzoom === 'number') minZoom = tj.minzoom;
          if (typeof tj.maxzoom === 'number') {
            // Request tiles all the way to the COG's native resolution. The old
            // z14 ceiling (~9 m/px) meant OpenLayers stretched one tile over
            // every deeper zoom, which is what made files look soft next to
            // QGIS. Tile pressure stays bounded by MAX_TILES_LOADING.
            maxZoom = Math.min(MAX_RASTER_ZOOM, tj.maxzoom);
          }
          if (tj.bounds?.length === 4) {
            fitBbox = tj.bounds;
            extent3857 = transformExtent(tj.bounds, 'EPSG:4326', 'EPSG:3857');
          }
        }
      } catch (err) {
        console.warn('[CanvasMapAdapter] tilejson fetch failed', err);
      }
    }

    // Paint immediately with provisional DEM stretch (no full-file stats wait).
    url = ensureTitilerPngTileTemplate(
      await colorizeDemTileTemplate(url, layer, { provisional: true }),
    );
    url = withDisplayTileSize(url);

    const source = createXyzSource(url, minZoom, maxZoom);
    const olLayer = new TileLayer({
      source,
      extent: extent3857,
      zIndex: 10 + this.layersById.size,
      opacity: 1,
      preload: 0,
      cacheSize: TILE_CACHE_SIZE,
      useInterimTilesOnError: false,
      properties: { geosoftId: layer.id },
    });
    this.map.addLayer(olLayer);
    this.layersById.set(layer.id, olLayer);
    this.rasterTileTemplates.set(layer.id, url);
    this.rasterZoom.set(layer.id, { min: minZoom, max: maxZoom });
    this.layerHealth.set(layer.id, 'ready');

    // Refine the colour stretch in the background (tiny max_size stats sample).
    // Goes through the shared colourise path so a derivative keeps its role
    // colormap and its masked nodata sentinel.
    if (isDemLikeLayer(layer)) {
      const layerId = layer.id;
      const baseUrl = url;
      void colorizeDemTileTemplate(baseUrl, layer).then((refinedUrl) => {
        if (!this.map || !this.layersById.has(layerId)) return;
        // The ToolCard may have rewritten the template while the stats sample
        // was in flight; replacing the source now would drop whatever it
        // applied, background keying included.
        if (this.rasterTileTemplates.get(layerId) !== baseUrl) return;
        const refined = withDisplayTileSize(refinedUrl);
        if (refined === baseUrl) return;
        const existing = this.layersById.get(layerId);
        if (!(existing instanceof TileLayer)) return;
        existing.setSource(createXyzSource(refined, minZoom, maxZoom));
        this.rasterTileTemplates.set(layerId, refined);
      });
    }

    if (!this.fitted && this.tryFitLonLatBbox(fitBbox)) {
      this.fitted = true;
    }
  }

  /**
   * Mount a feature layer: PMTiles (MVT) vector tiles, or a GeoJSON /
   * OGC-Features payload. Without this the Canvas engine dropped every
   * `featureLayers` item on the floor, so toggling one on did nothing.
   */
  private async mountVectorLayer(layer: Layer): Promise<void> {
    if (!this.map) return;

    if (this.layersById.has(layer.id)) {
      if (this.desiredVisibility.get(layer.id) !== false) {
        await this.setLayerVisible(layer.id, true);
      }
      this.layerHealth.set(layer.id, 'ready');
      return;
    }

    const pmtilesUrl =
      typeof layer.endpoints.pmtiles === 'string' && layer.endpoints.pmtiles.length > 0
        ? layer.endpoints.pmtiles
        : null;
    const featuresUrl =
      typeof layer.endpoints.features === 'string' && layer.endpoints.features.length > 0
        ? layer.endpoints.features
        : null;

    if (!pmtilesUrl && !featuresUrl) {
      console.warn('[CanvasMapAdapter] vector layer has no pmtiles/features endpoint', layer.id);
      this.layerHealth.set(layer.id, 'unsupported');
      return;
    }

    // Vectors draw above every raster overlay (zIndex 10 + n).
    const zIndex = 100 + this.layersById.size;
    try {
      if (pmtilesUrl) {
        const { olLayer, bounds, sourceLayers } = await createPmtilesVectorTileLayer(
          layer,
          pmtilesUrl,
          { zIndex },
        );
        console.info('[CanvasMapAdapter] PMTiles vector mount', {
          layerId: layer.id,
          url: pmtilesUrl,
          bounds,
          sourceLayers,
        });
        this.map.addLayer(olLayer);
        this.layersById.set(layer.id, olLayer);
        if (bounds) this.vectorBounds.set(layer.id, bounds);
        if (!this.fitted && this.tryFitLonLatBbox(bounds ?? layer.bbox)) {
          this.fitted = true;
        }
      } else if (featuresUrl) {
        const olLayer = await createGeoJsonVectorLayer(layer, featuresUrl, { zIndex });
        if (!olLayer) {
          this.layerHealth.set(layer.id, 'failed');
          return;
        }
        this.map.addLayer(olLayer);
        this.layersById.set(layer.id, olLayer);
        const extent = olLayer.getSource()?.getExtent();
        if (!this.fitted && extent && extent.every(Number.isFinite)) {
          this.map.getView().fit(extent, {
            padding: [48, 48, 48, 48],
            maxZoom: 14,
            duration: 0,
          });
          this.fitted = true;
        } else if (!this.fitted && this.tryFitLonLatBbox(layer.bbox)) {
          this.fitted = true;
        }
      }
      this.layerHealth.set(layer.id, 'ready');
    } catch (err) {
      console.warn('[CanvasMapAdapter] vector layer mount failed', layer.id, err);
      this.layerHealth.set(layer.id, 'failed');
      throw err;
    }
  }

  getLayerHealth(layerId: string): 'ready' | 'loading' | 'failed' | 'unsupported' {
    return this.layerHealth.get(layerId) ?? 'loading';
  }

  /** Frame a mounted vector layer's archive extent (zoom-to-layer). */
  flyToBbox(bbox: readonly number[], crs = 'EPSG:4326'): boolean {
    if (!this.map || bbox.length !== 4) return false;
    if (crs.toUpperCase() !== 'EPSG:4326' && crs.toUpperCase() !== 'OGC:CRS84') {
      return false;
    }
    return this.tryFitLonLatBbox(bbox);
  }

  getVectorBounds(layerId: string): readonly [number, number, number, number] | null {
    return this.vectorBounds.get(layerId) ?? null;
  }

  async removeLayer(layerId: string): Promise<void> {
    const olLayer = this.layersById.get(layerId);
    if (!olLayer || !this.map) return;
    const removingRaster = this.rasterTileTemplates.has(layerId);
    const listeners = this.rasterPrerender.get(layerId);
    if (listeners) {
      const target = asRenderEventTarget(olLayer);
      target.un('prerender', listeners.prerender);
      target.un('postrender', listeners.postrender);
      this.rasterPrerender.delete(layerId);
    }
    this.map.removeLayer(olLayer);
    this.layersById.delete(layerId);
    this.rasterTileTemplates.delete(layerId);
    this.rasterZoom.delete(layerId);
    this.vectorBounds.delete(layerId);
    this.layerHealth.delete(layerId);
    if (removingRaster) this.fitted = false;
  }

  /**
   * Apply the Raster ToolCard state to a mounted Canvas raster layer.
   * Opacity via `setOpacity`; brightness/contrast/saturation via canvas
   * filter on prerender; blend via `globalCompositeOperation`; bands /
   * colormap / nodata via TiTiler URL rewrite + source swap.
   */
  getRasterTileTemplate(layerId: string): string | null {
    return this.rasterTileTemplates.get(layerId) ?? null;
  }

  applyRasterToolCard(
    layerId: string,
    state: {
      readonly opacity: number;
      readonly brightness: number;
      readonly contrast: number;
      readonly saturation: number;
      readonly bands: readonly [number, number, number];
      readonly colorRamp: RasterColorRamp;
      readonly renderMode?: import('@/state/useViewerStore').RasterRenderMode;
      readonly noDataMask: boolean;
      readonly blending: RasterBlendMode;
    },
    options: {
      readonly multiBandImagery?: boolean;
      readonly nodata?: number | null;
      /** From `/cog/info`. Omit / 0 → display only; never rewrite tile bands. */
      readonly bandCount?: number;
      readonly preserveColormap?: boolean;
      readonly preserveNodata?: boolean;
    } = {},
  ): void {
    const olLayer = this.layersById.get(layerId);
    if (!olLayer) return;

    olLayer.setOpacity(Math.max(0, Math.min(1, state.opacity)));

    const renderEvents = asRenderEventTarget(olLayer);
    const previous = this.rasterPrerender.get(layerId);
    if (previous) {
      renderEvents.un('prerender', previous.prerender);
      renderEvents.un('postrender', previous.postrender);
    }
    const filter = openLayersCanvasFilterFromState(state);
    const composite = openLayersCompositeFromBlend(state.blending);
    const prerender = (event: { context?: CanvasRenderingContext2D }) => {
      const ctx = event.context;
      if (!ctx) return;
      ctx.save();
      ctx.filter = filter;
      if (composite) ctx.globalCompositeOperation = composite;
    };
    const postrender = (event: { context?: CanvasRenderingContext2D }) => {
      event.context?.restore();
    };
    renderEvents.on('prerender', prerender);
    renderEvents.on('postrender', postrender);
    this.rasterPrerender.set(layerId, { prerender, postrender });

    // Vector layers honour opacity + canvas filters, but have no TiTiler
    // template to rewrite.
    if (!(olLayer instanceof TileLayer)) {
      olLayer.changed();
      return;
    }

    if (!(typeof options.bandCount === 'number' && options.bandCount > 0)) {
      olLayer.changed();
      return;
    }

    const current = this.rasterTileTemplates.get(layerId);
    if (!current) return;
    const next = withDisplayTileSize(applyTitilerRasterStyleToTemplate(current, state, options));
    if (next === current) {
      olLayer.changed();
      return;
    }
    const zoom = this.rasterZoom.get(layerId) ?? { min: 0, max: MAX_RASTER_ZOOM };
    olLayer.setSource(createXyzSource(next, zoom.min, zoom.max));
    this.rasterTileTemplates.set(layerId, next);
  }

  /**
   * Vector ToolCard paint — stroke / fill / width on a mounted feature layer.
   * No-op when the id is a raster (the Raster card owns those).
   */
  setVectorPaint(
    layerId: string,
    delta: {
      readonly fillColor?: string;
      readonly fillOpacity?: number;
      readonly lineColor?: string;
      readonly lineWidth?: number;
      readonly lineSymbol?: LineSymbolState;
      readonly pointSize?: number;
      readonly pointSymbolId?: string;
      readonly pointRotation?: number;
      readonly polygonSymbol?: PolygonSymbolState;
      readonly uniqueValueSymbology?: UniqueValueSymbology | null;
    },
  ): void {
    const previous = this.vectorPaintById.get(layerId) ?? {};
    const next = { ...previous, ...delta };
    this.vectorPaintById.set(layerId, next);
    this.applyVectorStyle(layerId);
    // Registry icons live in lazily-imported sets. The style function above
    // falls back to a plain shape until they land, so re-style once they do.
    if (next.pointSymbolId && isPointIconId(next.pointSymbolId) && !peekPointIcon(next.pointSymbolId)) {
      void loadPointIcons()
        .then(() => this.applyVectorStyle(layerId))
        .catch(() => {
          /* picker already surfaces an unavailable icon set */
        });
    }
  }

  setVectorHighlight(layerId: string, featureId: VectorHighlightTarget): void {
    if (!isHighlightActive(featureId)) {
      this.vectorHighlightById.delete(layerId);
    } else {
      this.vectorHighlightById.set(layerId, featureId);
    }
    this.applyVectorStyle(layerId);
  }

  setIdentifyCursor(armed: boolean): void {
    this.identifyCursorArmed = armed;
    const viewport = this.map?.getViewport() ?? null;
    applyIdentifyCursor([viewport, this.host?.container], armed);
  }

  setVectorEditCursor(cursor: string): void {
    const viewport = this.map?.getViewport() ?? null;
    for (const element of [viewport, this.host?.container]) {
      if (element) element.style.cursor = cursor;
    }
  }

  setVectorFeatureVisible(
    layerId: string,
    featureId: string | number,
    visible: boolean,
    properties?: Readonly<Record<string, unknown>>,
  ): void {
    const source = this.vectorSourceFor(layerId);
    if (!source) return;
    const wanted = String(featureId);
    for (const feature of source.getFeatures()) {
      const id = feature.getId();
      const own = feature.getProperties();
      const idMatches =
        (id != null && String(id) === wanted)
        || ['id', 'OBJECTID', 'OBJECTID_1', 'objectid', 'FID', 'fid', 'gid']
          .some((key) => own[key] != null && String(own[key]) === wanted);
      const comparable = properties
        ? Object.entries(properties).filter(([key, value]) =>
          key.toLowerCase() !== 'x'
          && key.toLowerCase() !== 'y'
          && (typeof value === 'string' || typeof value === 'number')
          && own[key] != null,
        )
        : [];
      const propertiesMatch =
        comparable.length > 0
        && comparable.every(([key, value]) => String(own[key]) === String(value));
      if (idMatches || propertiesMatch) {
        feature.setStyle(visible ? undefined : []);
      }
    }
    source.changed();
  }

  setVectorEditMode(
    layerId: string,
    tool: VectorEditTool | null,
    geometryKind: IdentifyGeometryKind = 'unknown',
    features?: ReadonlyArray<ScaleSourceFeature>,
    selectedFeatureId?: string | number | null,
    onEdit?: (result: VectorEditResult) => void,
  ): void {
    this.clearVectorEditInteractions();
    this.setVectorEditCursor(editCursorForTool(tool));
    this.hydrateSessionOverlayFromStore(layerId);
    if (!this.map || !tool) return;
    this.editOnEdit = onEdit ?? null;
    if (tool === 'delete') {
      this.deleteSelectedVectorFeature(layerId, selectedFeatureId, onEdit);
      return;
    }
    const source = this.createEditWorkingSource(layerId, features);
    if (!source) return;
    const session = resolveVectorEditSession(tool, geometryKind);
    if (!session) return;
    try {
      if (session.terraMode === 'select') {
        this.editSelect = new Select(
          this.editWorkingLayer ? { layers: [this.editWorkingLayer] } : undefined,
        );
        this.map.addInteraction(this.editSelect);
        this.editInteractions.push(this.editSelect);
        if (selectedFeatureId != null) {
          const selected = source.getFeatures().find((feature) =>
            String(feature.getId() ?? feature.get('id')) === String(selectedFeatureId),
          );
          if (selected) this.editSelect.getFeatures().push(selected);
        }
        const modify = new Modify({ features: this.editSelect.getFeatures() });
        modify.on('modifyend', (event) => {
          event.features.forEach((feature) => {
            const exported = olEditFeatureToGeoJsonLonLat(feature) as VectorExportFeature;
            this.mirrorEditToSessionOverlay(layerId, {
              operation: 'update',
              feature: exported,
            });
            this.editOnEdit?.({ operation: 'update', feature: exported });
          });
        });
        this.map.addInteraction(modify);
        this.editInteractions.push(modify);
        if (session.scale) {
          const translate = new Translate({ features: this.editSelect.getFeatures() });
          this.map.addInteraction(translate);
          this.editInteractions.push(translate);
        }
      } else {
        const type =
          session.terraMode === 'linestring'
            ? 'LineString'
            : session.terraMode === 'polygon'
              ? 'Polygon'
              : 'Point';
        const draw = new Draw({ source, type });
        draw.on('drawend', (event) => {
          const feature = event.feature;
          if (feature.getId() == null) {
            feature.setId(`draw-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
          }
          const exported = olEditFeatureToGeoJsonLonLat(feature) as VectorExportFeature;
          this.mirrorEditToSessionOverlay(layerId, {
            operation: 'create',
            feature: exported,
          });
          this.editOnEdit?.({ operation: 'create', feature: exported });
        });
        this.map.addInteraction(draw);
        this.editInteractions.push(draw);
        if (session.snap) {
          const snap = new Snap({ source });
          this.map.addInteraction(snap);
          this.editInteractions.push(snap);
        }
      }
    } catch (err) {
      console.warn('[CanvasMapAdapter] setVectorEditMode failed', err);
    }
  }

  /**
   * Write a create / update / delete into the permanent session overlay layer
   * BEFORE the ToolCard callback fires, so the drawn polygon is on-screen the
   * moment the pointer is released — even if the user immediately clicks a
   * different tool and forces `clearVectorEditInteractions`.
   */
  private mirrorEditToSessionOverlay(layerId: string, result: VectorEditResult): void {
    if (!this.map) return;
    const source = this.ensureSessionOverlaySource(layerId);
    if (!source) return;
    const key = String(result.featureId ?? result.feature?.id ?? '');
    if (!key) return;
    const format = new GeoJSON();
    const existing = source.getFeatures().find((feature) => {
      const id = feature.getId() ?? feature.get('id');
      return id != null && String(id) === key;
    });
    if (existing) source.removeFeature(existing);
    if (result.operation === 'delete') return;
    if (!result.feature?.geometry) return;
    try {
      const olFeatures = format.readFeatures(
        {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              id: result.feature.id ?? key,
              properties: { ...(result.feature.properties ?? {}) },
              geometry: result.feature.geometry,
            },
          ],
        },
        { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
      );
      for (const olFeature of olFeatures) source.addFeature(olFeature);
    } catch (err) {
      console.warn('[CanvasMapAdapter] mirrorEditToSessionOverlay failed', err);
    }
  }

  /**
   * Drop one feature from the persistent session overlay and from the live
   * interaction layer. Used by the ToolCard's per-polygon delete row, which
   * names the victim explicitly instead of relying on the current selection.
   */
  removeVectorSessionFeature(layerId: string, featureId: string | number): void {
    if (!this.map) return;
    const key = String(featureId);
    if (!key) return;
    const sources = [
      this.sessionOverlayLayerId === layerId
        ? (this.sessionOverlayLayer?.getSource() as VectorSource | undefined)
        : undefined,
      this.editWorkingLayerId === layerId
        ? (this.editWorkingLayer?.getSource() as VectorSource | undefined)
        : undefined,
      this.vectorSourceFor(layerId) ?? undefined,
    ];
    for (const source of sources) {
      if (!source) continue;
      const victim = source.getFeatures().find((feature) => {
        const id = feature.getId() ?? feature.get('id');
        return id != null && String(id) === key;
      });
      if (victim) {
        try {
          source.removeFeature(victim);
        } catch {
          /* already removed */
        }
      }
    }
  }

  private ensureSessionOverlaySource(layerId: string): VectorSource | null {
    if (!this.map) return null;
    if (
      this.sessionOverlayLayer
      && this.sessionOverlayLayerId
      && this.sessionOverlayLayerId !== layerId
    ) {
      try {
        this.map.removeLayer(this.sessionOverlayLayer);
      } catch {
        /* already removed */
      }
      this.sessionOverlayLayer = null;
      this.sessionOverlayLayerId = null;
    }
    if (!this.sessionOverlayLayer) {
      const source = new VectorSource();
      this.sessionOverlayLayer = new VectorLayer({ source, zIndex: 999 });
      this.sessionOverlayLayerId = layerId;
      this.map.addLayer(this.sessionOverlayLayer);
    }
    return this.sessionOverlayLayer.getSource() as VectorSource;
  }

  /**
   * Fill the session overlay with every persisted feature (created + updated)
   * from `vectorEditSession`. Called on every `setVectorEditMode` so a Draw
   * commit survives any tool switch — the interaction layer can come and go
   * but the overlay always mirrors the store.
   */
  private hydrateSessionOverlayFromStore(layerId: string): void {
    if (!this.map) return;
    const persisted = getVectorSessionFeaturesForLayer(layerId);
    const source = this.ensureSessionOverlaySource(layerId);
    if (!source) return;
    source.clear();
    if (persisted.length === 0) return;
    const format = new GeoJSON();
    for (const entry of persisted) {
      const id = entry.feature.id ?? entry.feature.properties?.id;
      const key = id == null ? null : String(id);
      if (!key) continue;
      try {
        const olFeatures = format.readFeatures(
          {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                id: entry.feature.id ?? key,
                properties: { ...(entry.feature.properties ?? {}) },
                geometry: entry.feature.geometry,
              },
            ],
          },
          { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
        );
        for (const olFeature of olFeatures) source.addFeature(olFeature);
      } catch (err) {
        console.warn('[CanvasMapAdapter] hydrateSessionOverlayFromStore item failed', err);
      }
    }
  }

  deleteSelectedVectorFeature(
    layerId: string,
    selectedFeatureId?: string | number | null,
    onEdit?: (result: VectorEditResult) => void,
  ): void {
    const callback = onEdit ?? this.editOnEdit;
    const source =
      this.editWorkingLayerId === layerId
        ? this.editWorkingLayer?.getSource()
        : this.vectorSourceFor(layerId);
    if (!source) {
      if (selectedFeatureId != null) {
        callback?.({ operation: 'delete', featureId: selectedFeatureId });
      }
      return;
    }
    const selected = this.editSelect?.getFeatures().getArray() ?? [];
    const highlightId = this.vectorHighlightById.get(layerId);
    const victims =
      selected.length > 0
        ? selected
        : source.getFeatures().filter((feature) => {
            if (highlightId == null || typeof highlightId === 'object') return false;
            const id = feature.getId();
            return String(id) === String(highlightId);
          });
    for (const feature of victims) {
      const id = feature.getId() ?? feature.get('id');
      source.removeFeature(feature);
      if (typeof id === 'string' || typeof id === 'number') {
        this.mirrorEditToSessionOverlay(layerId, { operation: 'delete', featureId: id });
        callback?.({ operation: 'delete', featureId: id });
      }
    }
    if (victims.length === 0 && selectedFeatureId != null) {
      this.mirrorEditToSessionOverlay(layerId, {
        operation: 'delete',
        featureId: selectedFeatureId,
      });
      callback?.({ operation: 'delete', featureId: selectedFeatureId });
    }
    this.setVectorEditCursor('');
  }

  private createEditWorkingSource(
    layerId: string,
    features?: ReadonlyArray<ScaleSourceFeature>,
  ): VectorSource | null {
    if (!this.map) return null;
    const normalized = scaleFeaturesFrom(features);
    if (normalized.length === 0) return this.vectorSourceFor(layerId);
    const format = new GeoJSON();
    const source = new VectorSource({
      features: format.readFeatures(
        {
          type: 'FeatureCollection',
          features: normalized.map((feature, index) => ({
            type: 'Feature',
            id: feature.id ?? feature.properties?.id ?? `edit-${index}`,
            properties: feature.properties ?? {},
            geometry: feature.geometry,
          })),
        },
        { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
      ),
    });
    this.editWorkingLayer = new VectorLayer({ source });
    this.editWorkingLayerId = layerId;
    this.map.addLayer(this.editWorkingLayer);
    return source;
  }

  private vectorSourceFor(layerId: string): VectorSource | null {
    const olLayer = this.layersById.get(layerId);
    if (!(olLayer instanceof VectorLayer)) return null;
    const source = olLayer.getSource();
    return source instanceof VectorSource ? source : null;
  }

  private clearVectorEditInteractions(): void {
    if (!this.map) {
      this.editInteractions = [];
      this.editSelect = null;
      this.editWorkingLayer = null;
      this.editWorkingLayerId = null;
      this.editOnEdit = null;
      return;
    }
    for (const interaction of this.editInteractions) {
      try {
        this.map.removeInteraction(interaction);
      } catch {
        /* already removed */
      }
    }
    this.editInteractions = [];
    this.editSelect = null;
    if (this.editWorkingLayer) {
      try {
        this.map.removeLayer(this.editWorkingLayer);
      } catch {
        /* already removed */
      }
    }
    this.editWorkingLayer = null;
    this.editWorkingLayerId = null;
    this.editOnEdit = null;
  }

  private applyVectorStyle(layerId: string): void {
    const olLayer = this.layersById.get(layerId);
    if (!olLayer) return;
    if (!(olLayer instanceof VectorLayer) && !(olLayer instanceof VectorTileLayer)) return;
    const delta = this.vectorPaintById.get(layerId) ?? {};
    try {
      olLayer.setStyle(
        vectorStyleFunctionFromPaint(delta, {
          selectedFeatureId: this.vectorHighlightById.get(layerId) ?? null,
        }),
      );
    } catch (err) {
      console.warn('[CanvasMapAdapter] setVectorPaint failed', layerId, err);
    }
  }

  /**
   * Attribute filter for GeoJSON vector sources. Vector tiles keep the
   * last style; a full MVT expression filter is out of scope here.
   */
  setVectorFilter(layerId: string, filter: unknown): void {
    const olLayer = this.layersById.get(layerId);
    if (!(olLayer instanceof VectorLayer)) return;
    const source = olLayer.getSource();
    if (!(source instanceof VectorSource)) return;
    try {
      const active = Boolean(filter && Array.isArray(filter) && filter.length > 0);
      source.forEachFeature((feature) => {
        if (!active) {
          feature.setStyle(undefined);
          return;
        }
        const visible = featureMatchesSimpleFilter(feature.getProperties(), filter);
        feature.setStyle(visible ? undefined : []);
      });
    } catch (err) {
      console.warn('[CanvasMapAdapter] setVectorFilter failed', layerId, err);
    }
  }

  getVectorFeatures(layerId: string): ReadonlyArray<{
    readonly id?: string | number;
    readonly properties: Readonly<Record<string, unknown>>;
    readonly geometry?: unknown;
  }> {
    const olLayer = this.layersById.get(layerId);
    if (olLayer instanceof VectorLayer) {
      const source = olLayer.getSource();
      if (!(source instanceof VectorSource)) return [];
      return source.getFeatures().map((feature) => olFeatureToExport(feature));
    }
    if (olLayer instanceof VectorTileLayer && this.map) {
      const source = olLayer.getSource() as
        | { getFeaturesInExtent?: (extent: Extent) => Array<{ getId?: () => unknown; getProperties?: () => Record<string, unknown>; getGeometry?: () => unknown }> }
        | null;
      const size = this.map.getSize();
      const extent = size ? this.map.getView().calculateExtent(size) : null;
      if (!source?.getFeaturesInExtent || !extent) return [];
      try {
        return source.getFeaturesInExtent(extent).map((feature) => olFeatureToExport(feature));
      } catch {
        return [];
      }
    }
    return [];
  }

  zoomToVectorBounds(bounds: readonly [number, number, number, number]): void {
    this.fitLonLatBbox(bounds);
  }

  getLastVectorPickProperties(): Readonly<Record<string, unknown>> | null {
    return this.lastVectorPickProperties;
  }

  private pickVectorFeatureAtPixel(
    pixel: number[],
  ): {
    readonly layerId: string;
    readonly featureId: string | number;
    readonly properties: Readonly<Record<string, unknown>>;
  } | null {
    const map = this.map;
    if (!map) return null;
    let found: {
      readonly layerId: string;
      readonly featureId: string | number;
      readonly properties: Readonly<Record<string, unknown>>;
    } | null = null;
    map.forEachFeatureAtPixel(
      pixel,
      (feature, layer) => {
        if (!layer) return undefined;
        const layerId = layer.get('geosoftId');
        if (!isPickedVectorLayerId(layerId)) return undefined;
        if (!(layer instanceof VectorLayer) && !(layer instanceof VectorTileLayer)) {
          return undefined;
        }
        const exported = olFeatureToExport(feature);
        const featureId = vectorPickIdFrom(exported.id, exported.properties);
        if (featureId == null) return undefined;
        found = {
          layerId,
          featureId,
          properties: exported.properties,
        };
        return true;
      },
      {
        hitTolerance: 6,
        layerFilter: (layer) => isPickedVectorLayerId(layer.get('geosoftId')),
      },
    );
    return found;
  }

  async setLayerVisible(layerId: string, visible: boolean): Promise<void> {
    this.desiredVisibility.set(layerId, visible);
    const olLayer = this.layersById.get(layerId);
    if (!olLayer) return;
    olLayer.setVisible(visible);
  }

  setBlockVisible(): void {
    /* no-op — 2D canvas fallback */
  }

  setBlockOpacity(): void {
    /* no-op */
  }

  setTime(): void {
    /* no-op */
  }

  flyTo(camera: ViewerCamera): void {
    if (!this.map) return;
    const [lon, lat] = camera.lonLat ?? [55.27, 25.2];
    const zoom = camera.zoom ?? 10;
    // Instant jump — animated fly loads tiles at every intermediate zoom.
    this.map.getView().setCenter(fromLonLat([lon, lat]));
    this.map.getView().setZoom(zoom);
  }

  /** Current map viewport as EPSG:4326 `[west, south, east, north]`. */
  getViewportLonLatBbox(): readonly [number, number, number, number] | null {
    if (!this.map) return null;
    const size = this.map.getSize();
    if (!size || size[0] <= 0 || size[1] <= 0) return null;
    const extent = this.map.getView().calculateExtent(size);
    const [minX, minY, maxX, maxY] = transformExtent(extent, 'EPSG:3857', 'EPSG:4326');
    if (![minX, minY, maxX, maxY].every(Number.isFinite) || maxX <= minX || maxY <= minY) {
      return null;
    }
    return [minX, minY, maxX, maxY];
  }

  getActiveFrameCrs(): string | null {
    return 'EPSG:3857';
  }

  private tryFitLonLatBbox(
    bbox: readonly number[] | null | undefined,
  ): boolean {
    if (!this.map || !bbox || bbox.length < 4) return false;
    const [minX, minY, maxX, maxY] = bbox;
    if (![minX, minY, maxX, maxY].every(Number.isFinite)) return false;
    const lonSpan = maxX - minX;
    const latSpan = maxY - minY;
    if (!(maxX > minX && maxY > minY) || lonSpan >= 359 || latSpan >= 169) {
      return false;
    }
    if (
      Math.abs(minX) > 180
      || Math.abs(maxX) > 180
      || Math.abs(minY) > 90
      || Math.abs(maxY) > 90
    ) {
      return false;
    }
    this.fitLonLatBbox(bbox);
    return true;
  }

  private fitLonLatBbox(
    bbox: readonly [number, number, number, number] | readonly number[],
  ): void {
    if (!this.map) return;
    const [minX, minY, maxX, maxY] = bbox;
    if (![minX, minY, maxX, maxY].every(Number.isFinite)) return;
    const lonSpan = maxX - minX;
    const latSpan = maxY - minY;
    if (lonSpan >= 359 || latSpan >= 169) return;
    const extent = transformExtent([minX, minY, maxX, maxY], 'EPSG:4326', 'EPSG:3857');
    const span = Math.max(lonSpan, latSpan);
    let maxZoom = 12;
    if (span < 0.08) maxZoom = 11;
    if (span < 0.03) maxZoom = 10;
    if (span < 0.015) maxZoom = 9;
    // duration: 0 → one viewport of tiles, not a zoom-animation tile storm.
    this.map.getView().fit(extent, {
      padding: [48, 48, 48, 48],
      maxZoom,
      duration: 0,
    });
  }
}
